import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { Modal } from '@/components/Modal';
import { ActiveAccountPicker } from '@/components/ActiveAccountPicker';
import { NewChatDialog } from '@/components/NewChatDialog';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { LoadErrorState } from '@/components/ListStates';
import { ConversationTranscript, TranscriptMessage } from '@/components/leads/ConversationTranscript';
import { AiDebugPanel } from '@/components/leads/AiDebugPanel';
import { TemplatePickerModal } from '@/components/leads/TemplatePickerModal';
import { WhatsAppAssetPicker } from '@/components/leads/WhatsAppAssetPicker';
import { CatalogPickerModal } from '@/components/leads/CatalogPickerModal';
import { MediaLibraryModal } from '@/components/MediaLibraryModal';
import { leadsService, type Lead, type LeadDetail, type LeadStatus } from '@/services/leads.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import { useAuth } from '@/hooks/useAuth';
import { uploadsService } from '@/services/uploads.service';
import { Search, SearchX, Send, Loader2, ExternalLink, Bot, BotOff, MessageCircle, Paperclip, X, Bell, BellOff, Sparkles, ArrowLeft, FileText, Clock, ShoppingBag, Images, MessageSquarePlus, Trash2, ShieldCheck, Wifi, WifiOff } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ConversationListSkeleton } from '@/components/PageSkeletons';

// SSE delivers inbound messages instantly; polling is now a safety-net at low frequency.
const POLL_LIST_MS = 30_000;

const MUTED_LEADS_KEY = 'slighter.notifications.muted-leads';

function readMutedLeads(): string[] {
  try {
    const raw = localStorage.getItem(MUTED_LEADS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}

function writeMutedLeads(arr: string[]) {
  try { localStorage.setItem(MUTED_LEADS_KEY, JSON.stringify(arr)); }
  catch { /* ignore quota */ }
}
// Thread refreshes happen via SSE on inbound; poll once per 20s as a backup.
const POLL_THREAD_MS = 8_000;

const STATUS_COLORS: Record<LeadStatus, string> = {
  new:        'bg-info-soft text-info',
  contacted:  'bg-warning-soft text-warning',
  qualified:  'bg-primary-soft text-primary',
  won:        'bg-success-soft text-success',
  lost:       'bg-surface-alt text-text-muted',
  spam:       'bg-danger-soft text-danger',
};

function buildTranscript(detail: LeadDetail): TranscriptMessage[] {
  const out: TranscriptMessage[] = [];
  for (const ev of detail.leadEvents) {
    if (ev.type === 'inbound_message') {
      out.push({
        id: ev.eventUid,
        role: 'inbound',
        body: (ev.payload.body as string) ?? undefined,
        mediaType: (ev.payload.mediaType as string) ?? undefined,
        mediaUrl: (ev.payload.mediaUrl as string) ?? undefined,
        mediaFilename: (ev.payload.mediaFilename as string) ?? undefined,
        mediaStorageStatus: ev.payload.mediaStorageStatus as string | undefined,
        location: ev.payload.location as TranscriptMessage['location'],
        flowResponse: ev.payload.flowResponse as TranscriptMessage['flowResponse'],
        audioUrl: (ev.payload.audioUrl as string) ?? undefined,
        audioMime: (ev.payload.audioMime as string) ?? undefined,
        audioDurationSec: (ev.payload.audioDurationSec as number) ?? undefined,
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'outbound_message') {
      out.push({
        id: ev.eventUid,
        role: 'outbound',
        body: (ev.payload.body as string) ?? undefined,
        mediaType: (ev.payload.mediaType as string) ?? undefined,
        mediaUrl: (ev.payload.mediaUrl as string) ?? undefined,
        footer: (ev.payload.footer as string) ?? undefined,
        buttons: (ev.payload.buttons as { type: string; text?: string }[]) ?? undefined,
        audioUrl: (ev.payload.audioUrl as string) ?? undefined,
        audioMime: (ev.payload.audioMime as string) ?? undefined,
        timestamp: ev.createdAt,
        isBot: ev.actorKind === 'bot' || ev.actorKind === 'ai',
        source: (ev.payload.source as string) ?? undefined,
        deliveryStatus: (ev.payload.deliveryStatus as TranscriptMessage['deliveryStatus']) ?? undefined,
        failureReason: (ev.payload.failureReason as string) ?? undefined,
      });
    } else if (ev.type === 'note_added') {
      out.push({ id: ev.eventUid, role: 'system', body: `Note: ${(ev.payload.body as string) ?? ''}`, timestamp: ev.createdAt });
    } else if (ev.type === 'status_changed') {
      out.push({ id: ev.eventUid, role: 'system', body: `Status: ${ev.payload.from} → ${ev.payload.to}`, timestamp: ev.createdAt });
    } else if (ev.type === 'handoff_to_human') {
      out.push({ id: ev.eventUid, role: 'system', body: '🤝 Handed off to human', timestamp: ev.createdAt });
    } else if (ev.type === 'bot_paused_by_user') {
      out.push({ id: ev.eventUid, role: 'system', body: '⏸️ Bot paused', timestamp: ev.createdAt });
    } else if (ev.type === 'bot_resumed') {
      out.push({ id: ev.eventUid, role: 'system', body: '▶️ Bot resumed', timestamp: ev.createdAt });
    } else if (ev.type === 'ai_tool_call') {
      const toolName = (ev.payload.toolName as string) ?? 'tool';
      const result = ev.payload.result as { ok?: boolean; message?: string } | undefined;
      const verb = result?.ok ? '✨' : '⚠️';
      const summary = result?.message ?? toolName;
      out.push({
        id: ev.eventUid,
        role: 'system',
        body: `${verb} AI · ${summary}`,
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'flow_started') {
      const flowName = (ev.payload.flowName as string) ?? 'Bot flow';
      const trig = ev.payload.trigger as Record<string, unknown> | undefined;
      const channel = trig?.channel as string | undefined;
      const triggerType = trig?.type as string | undefined;
      const matchedKeyword = trig?.matchedKeyword as string | undefined;
      const adId = trig?.adId as string | undefined;
      const campaignId = trig?.campaignId as string | undefined;
      const formId = trig?.formId as string | undefined;
      const channelLabel: Record<string, string> = {
        facebook_ads:     '📘 Facebook ad',
        instagram_ads:    '📷 Instagram ad',
        meta_ads:         '🅵 Meta ad',
        meta_lead_form:   '📋 Meta lead form',
        tiktok_lead_form: '🎵 TikTok lead form',
        google_lead_form: '🔎 Google lead form',
        google_ads:       '🔎 Google ad',
        web_form:         '🌐 Web form',
        email:            '✉️ Email',
        whatsapp_inbound: '💬 WhatsApp inbound',
        manual:           '✋ Manual',
      };
      const channelStr = channel ? (channelLabel[channel] ?? channel) : '';
      const detailParts: string[] = [];
      if (triggerType === 'keyword' && matchedKeyword) detailParts.push(`keyword "${matchedKeyword}"`);
      if (triggerType === 'ad' && adId) detailParts.push(`ad ${adId}`);
      if (triggerType === 'ad' && !adId && campaignId) detailParts.push(`campaign ${campaignId}`);
      if (triggerType === 'lead_ad_form' && formId) detailParts.push(`form ${formId}`);
      const detailStr = detailParts.length ? ` · ${detailParts.join(', ')}` : '';
      out.push({
        id: ev.eventUid,
        role: 'system',
        body: `🤖 Bot started: ${flowName}${channelStr ? ` (${channelStr})` : ''}${detailStr}`,
        timestamp: ev.createdAt,
      });
    }
  }
  return out;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function leadDisplayName(l: Lead): string {
  return l.profileName?.trim() || l.phone;
}

function leadInitials(l: Lead): string {
  const name = l.profileName?.trim();
  if (!name) return l.phone.slice(-2);
  const parts = name.split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || name[0].toUpperCase();
}

export default function Chats() {
  const [params, setParams] = useSearchParams();
  const selectedLeadUid = params.get('lead');
  const { active: activeAccount, loading: accountsLoading } = useActiveAccount();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';

  // AI debug panel — when set, the slide-over opens for that LeadEvent uid.
  const [debugLeadEventUid, setDebugLeadEventUid] = useState<string | null>(null);

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(false);
  const [filter, setFilter] = useState<'' | LeadStatus>('');
  const [q, setQ] = useState('');
  const listFiltered = !!q.trim() || !!filter;

  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);

  // Pending media attachment (uploaded to ImageKit; sent on next reply)
  const [pendingMedia, setPendingMedia] = useState<{
    url: string;
    type: 'image' | 'video' | 'document' | 'audio';
    mime?: string;
    filename?: string;
  } | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Approved-template picker — the only way to message outside the 24h window.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // Catalog picker — send a custom object record (menu item, service…) as a
  // formatted free-form message; gated by the same 24h window as the composer.
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [libraryMenuOpen, setLibraryMenuOpen] = useState(false);
  const [libraryType, setLibraryType] = useState<'image' | 'video' | 'document' | 'audio'>('image');

  // Per-conversation mute state — read from localStorage on render. Bumping
  // this counter triggers a re-read after toggling.
  const [muteVersion, setMuteVersion] = useState(0);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [streamState, setStreamState] = useState<'connecting' | 'live' | 'polling'>('connecting');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const selectedRef = useRef(selectedLeadUid);
  selectedRef.current = selectedLeadUid;
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  const isThreadMuted = useMemo(() => {
    if (!selectedLeadUid) return false;
    return readMutedLeads().includes(selectedLeadUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeadUid, muteVersion]);

  const toggleThreadMute = () => {
    if (!selectedLeadUid) return;
    const list = readMutedLeads();
    const next = list.includes(selectedLeadUid)
      ? list.filter((id) => id !== selectedLeadUid)
      : [...list, selectedLeadUid];
    writeMutedLeads(next);
    setMuteVersion((v) => v + 1);
    toast.success(next.includes(selectedLeadUid) ? 'Conversation muted' : 'Conversation unmuted');
  };

  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── List polling ────────────────────────────────────────────────────────
  const refreshList = async () => {
    try {
      const r = await leadsService.list({
        q: q || undefined,
        conversationsOnly: '1',
        status: filter || undefined,
        accountUid: activeAccount?.accountUid,
        limit: 100,
      });
      setLeads(r.items);
      setListError(false);
    } catch { setListError(true); } finally { setLoadingList(false); }
  };

  useEffect(() => {
    // Wait for the active account to resolve. The list endpoint treats a
    // missing accountUid as "no filter" (leads.service.ts:93), so firing while
    // the picker is still loading briefly showed leads from EVERY connected
    // WhatsApp number before the scoped refetch replaced them.
    if (accountsLoading) { setLoadingList(true); return; }
    setLoadingList(true);
    refreshList();
    const t = window.setInterval(refreshList, POLL_LIST_MS);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filter, activeAccount?.accountUid, accountsLoading]);

  // `/chats?phone=9715…` (from a booking, a contact…) opens that number's thread once the account is known.
  const wantedPhone = params.get('phone');
  useEffect(() => {
    if (!wantedPhone || accountsLoading) return;
    let cancelled = false;
    const digits = wantedPhone.replace(/\D+/g, '');
    (async () => {
      try {
        const r = await leadsService.list({ q: digits, accountUid: activeAccount?.accountUid, limit: 10 });
        if (cancelled) return;
        const match = r.items.find((l) => (l.phone ?? '').replace(/\D+/g, '') === digits);
        if (match) { setParams({ lead: match.leadUid }, { replace: true }); return; }
        toast.error(`No WhatsApp conversation with +${digits} yet`);
      } catch { /* the list is still usable */ }
      if (!cancelled) setParams({}, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedPhone, accountsLoading, activeAccount?.accountUid]);

  // ── Thread loading + polling ────────────────────────────────────────────
  const loadThread = async (leadUid: string, silent = false) => {
    if (!silent) setLoadingThread(true);
    try {
      const d = await leadsService.get(leadUid);
      if (selectedRef.current !== leadUid) return;
      setDetail((previous) => {
        if (previous?.lead.leadUid !== leadUid || previous.leadEvents.length <= 100) return d;
        const merged = new Map([...previous.leadEvents, ...d.leadEvents].map((event) => [event.eventUid, event]));
        return { ...d, leadEvents: Array.from(merged.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt)), hasOlderEvents: previous.hasOlderEvents };
      });
    } catch { /* interceptor surfaces */ } finally { if (selectedRef.current === leadUid) setLoadingThread(false); }
  };

  useEffect(() => {
    if (!selectedLeadUid) { setDetail(null); return; }
    setDetail(null);
    setComposerText('');
    setPendingMedia(null);
    stickToBottom.current = true;
    loadThread(selectedLeadUid);
    // Best-effort: tell Meta we've read the latest inbound so the customer
    // sees blue ticks. Errors are swallowed silently.
    leadsService.markRead(selectedLeadUid).catch(() => undefined);
    const t = window.setInterval(() => { if (!document.hidden) loadThread(selectedLeadUid, true); }, POLL_THREAD_MS);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeadUid]);

  useEffect(() => {
    const stream = leadsService.openConversationStream();
    if (!stream) { setStreamState('polling'); return; }
    stream.onopen = () => setStreamState('live');
    stream.onerror = () => setStreamState('polling');
    stream.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'polling_fallback') { setStreamState('polling'); return; }
        if (payload.type !== 'conversation_changed') return;
        refreshList();
        if (payload.leadUid === selectedRef.current) loadThread(payload.leadUid, true);
      } catch { /* ignore malformed server events */ }
    };
    return () => stream.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccount?.accountUid, q, filter]);

  useEffect(() => {
    if (stickToBottom.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail?.leadEvents]);

  const loadOlder = async () => {
    if (!detail?.leadEvents[0] || !selectedLeadUid) return;
    setLoadingOlder(true);
    const scrollHeight = scrollRef.current?.scrollHeight ?? 0;
    try {
      const response = await leadsService.olderMessages(selectedLeadUid, detail.leadEvents[0].eventUid);
      if (selectedRef.current !== selectedLeadUid) return;
      stickToBottom.current = false;
      setDetail((previous) => previous ? { ...previous, leadEvents: [...response.items, ...previous.leadEvents], hasOlderEvents: response.hasMore } : previous);
      window.requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop += scrollRef.current.scrollHeight - scrollHeight; });
    } finally { setLoadingOlder(false); }
  };

  const deleteConversation = async () => {
    if (!selectedLeadUid) return;
    setDeleting(true);
    try {
      await leadsService.deleteConversation(selectedLeadUid);
      setParams({}); setDetail(null); setDeleteOpen(false);
      await refreshList();
      toast.success('Conversation deleted from CRM');
    } finally { setDeleting(false); }
  };

  // ── Send reply ──────────────────────────────────────────────────────────
  const send = async () => {
    if (!selectedLeadUid || sending) return;
    if (!pendingMedia && !composerText.trim()) return;
    const body = composerText.trim();
    const media = pendingMedia;
    setSending(true);
    setComposerText('');
    setPendingMedia(null);
    try {
      const r = await leadsService.reply(selectedLeadUid, {
        body: body || undefined,
        clientRequestId: window.crypto.randomUUID(),
        mediaUrl: media?.type === 'audio' ? undefined : media?.url,
        mediaType: media?.type === 'audio' ? undefined : media?.type,
        audioUrl: media?.type === 'audio' ? media.url : undefined,
        audioMime: media?.mime,
        mediaFilename: media?.filename,
      });
      if (!r.ok) {
        if (r.error === 'OUTSIDE_24H_WINDOW') {
          // Backend refused before hitting Meta — steer to a template send.
          toast.error('The 24-hour reply window has closed — send an approved template instead.');
          setComposerText(body);
          if (media) setPendingMedia(media);
          setTemplatePickerOpen(true);
        } else {
          toast.error(r.error ?? 'Send failed');
          setComposerText(body);
          if (media) setPendingMedia(media);
        }
      }
      stickToBottom.current = true;
      await loadThread(selectedLeadUid, true);
      await refreshList();
    } catch (e: any) {
      // axios interceptor already toasted backend message
      setComposerText(body);
      if (media) setPendingMedia(media);
    } finally { setSending(false); composerRef.current?.focus(); }
  };

  // ── Attach + upload media ───────────────────────────────────────────────
  const onAttachClick = () => fileInputRef.current?.click();
  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again re-triggers
    if (!file) return;

    // Decide WhatsApp media kind from the file's MIME prefix
    const mime = file.type || 'application/octet-stream';
    let mediaType: 'image' | 'video' | 'document' | 'audio';
    if (mime.startsWith('image/')) mediaType = 'image';
    else if (mime.startsWith('video/')) mediaType = 'video';
    else if (mime.startsWith('audio/')) mediaType = 'audio';
    else mediaType = 'document';

    // Meta size caps: image 5MB, video 16MB, document 100MB
    const cap = mediaType === 'image' ? 5 : mediaType === 'document' ? 50 : 16;
    if (file.size > cap * 1024 * 1024) {
      toast.error(`${mediaType} too large (max ${cap}MB)`);
      return;
    }

    setUploadingMedia(true);
    try {
      const r = await uploadsService.imagekit(file, `whatsapp/chat/${mediaType === 'image' ? 'images' : mediaType === 'video' ? 'videos' : mediaType === 'audio' ? 'audio' : 'documents'}`, `whatsapp,${mediaType}`);
      setPendingMedia({ url: r.url, type: mediaType, mime, filename: file.name });
    } catch (err: any) {
      if (!err?.response) toast.error(err?.message || 'Upload failed');
    } finally { setUploadingMedia(false); }
  };

  const onComposerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending && composerEnabled) send();
    }
  };

  const transcript = useMemo(() => (detail ? buildTranscript(detail) : []), [detail]);
  const selectedLead = useMemo(() => leads.find((l) => l.leadUid === selectedLeadUid) ?? detail?.lead ?? null, [leads, selectedLeadUid, detail]);

  // 24h customer-service window — free-form replies only deliver while the
  // customer's last message is <24h old. Recomputed on every thread load;
  // the backend re-checks too, this is just the early UX signal.
  const windowOpen = useMemo(() => {
    const lastIn = detail?.lead.lastInboundAt;
    if (!lastIn) return false;
    return Date.now() - new Date(lastIn).getTime() < 24 * 60 * 60 * 1000;
  }, [detail?.lead.lastInboundAt]);
  // Legacy leads predating the lastInboundAt field would lock the composer
  // incorrectly — treat "field absent but inbound events exist" as unknown
  // and leave the composer enabled (backend still guards the actual send).
  const windowUnknown = !detail?.lead.lastInboundAt
    && (detail?.leadEvents ?? []).some((e) => e.type === 'inbound_message');
  const composerEnabled = windowOpen || windowUnknown;

  return (
    <>
      <WhatsAppHeader title="Inbox" section="chats" description="Every customer conversation, in one shared workspace."
        actions={<span className={clsx('inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium', streamState === 'live' ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted')}>
          {streamState === 'live' ? <Wifi size={13} /> : <WifiOff size={13} />}
          {streamState === 'live' ? 'Live updates' : streamState === 'connecting' ? 'Connecting…' : 'Syncing every 8s'}
        </span>}
      />

      {/* On mobile (<md), exactly ONE pane is visible:
          – no lead selected → show inbox list
          – lead selected → show chat pane (back arrow returns to list)
          On md+, both panes render side-by-side as before. */}
      <div className="mx-5 mb-5 flex h-[calc(100dvh-202px)] min-h-[480px] overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
        {/* ── Left: conversation list ──────────────────────────────────── */}
        <div className={clsx(
          'flex-col border-r border-border bg-surface',
          'md:flex md:w-[330px] lg:w-[360px] md:shrink-0',
          selectedLeadUid ? 'hidden md:flex' : 'flex w-full',
        )}>
          <div className="space-y-3 border-b border-border p-4">
            {/* Which number these conversations belong to — moved out of the
                sidebar, where it was far from the messages it governs. */}
            <ActiveAccountPicker variant="panel" />
            <Button
              variant="primary"
              className="w-full"
              onClick={() => setNewChatOpen(true)}
              iconLeft={<MessageSquarePlus size={14} />}
            >
              New conversation
            </Button>
            <Input
              placeholder="Search conversations…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              iconLeft={<Search size={14} />}
            />
            <div className="flex gap-1.5 overflow-x-auto">
              {[
                { v: '', label: 'All' },
                { v: 'new', label: 'New' },
                { v: 'contacted', label: 'Contacted' },
                { v: 'qualified', label: 'Qualified' },
                { v: 'won', label: 'Won' },
              ].map((f) => (
                <button
                  key={f.v}
                  onClick={() => setFilter(f.v as '' | LeadStatus)}
                  className={clsx(
                    'rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors',
                    filter === f.v ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted hover:text-text-primary',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingList && leads.length === 0 && (
              <ConversationListSkeleton rows={8} />
            )}

            {!loadingList && leads.length === 0 && listError && (
              <LoadErrorState
                bare
                what="your conversations"
                onRetry={() => { setLoadingList(true); void refreshList(); }}
                className="h-full justify-center"
              />
            )}

            {!loadingList && leads.length === 0 && !listError && listFiltered && (
              <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary shadow-soft">
                  <SearchX size={26} />
                </div>
                <p className="text-sm font-semibold text-text-primary">No conversations match your filters</p>
                <p className="mt-1 max-w-[260px] text-xs text-text-muted">
                  Try a different search or status, or clear the filters to see every conversation.
                </p>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => { setQ(''); setFilter(''); }}>
                  Clear filters
                </Button>
              </div>
            )}

            {!loadingList && leads.length === 0 && !listError && !listFiltered && (
              <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary shadow-soft">
                  <MessageCircle size={26} />
                </div>
                <p className="text-sm font-semibold text-text-primary">No conversations yet</p>
                <p className="mt-1 max-w-[260px] text-xs text-text-muted">
                  Inbound WhatsApp messages and click-to-WhatsApp ad replies show up here.
                  Get the first one rolling:
                </p>
                <div className="mt-4 flex flex-col gap-1.5 text-xs">
                  <Link
                    to={activeAccount ? '/whatsapp-account' : '/whatsapp-accounts'}
                    className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                  >
                    {activeAccount ? 'Send a test from WhatsApp Manager →' : 'Connect a WhatsApp account →'}
                  </Link>
                  <Link to="/templates" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                    Set up your first template →
                  </Link>
                  <Link to="/facebook/workspace" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                    Run a click-to-WhatsApp ad →
                  </Link>
                </div>
              </div>
            )}

            {leads.map((l) => {
              const active = l.leadUid === selectedLeadUid;
              return (
                <button
                  key={l.leadUid}
                  onClick={() => setParams({ lead: l.leadUid })}
                  className={clsx(
                    'flex w-full items-start gap-2.5 border-b border-border/60 px-3 py-3 text-left transition-colors',
                    active ? 'bg-primary-soft' : 'hover:bg-surface-alt',
                  )}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-soft text-xs font-semibold text-success">
                    {leadInitials(l)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[13.5px] font-semibold text-text-primary">{leadDisplayName(l)}</span>
                      <span className="shrink-0 text-2xs text-text-muted">{shortTime(l.lastMessage?.createdAt || l.updatedAt)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className={clsx('rounded-full px-1.5 py-0.5 text-2xs font-medium capitalize', STATUS_COLORS[l.status])}>
                        {l.status}
                      </span>
                      {l.botFlowUid && (
                        <span className="inline-flex items-center gap-0.5 text-2xs text-text-muted" title="Bot active">
                          <Bot size={10} />
                        </span>
                      )}
                    </div>
                    <div className="mt-1 truncate text-xs text-text-muted">{l.lastMessage ? `${l.lastMessage.direction === 'outbound_message' ? 'You: ' : ''}${l.lastMessage.body || (l.lastMessage.mediaType ? `${l.lastMessage.mediaType} attachment` : 'Message')}` : l.phone}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Right: thread + composer ─────────────────────────────────── */}
        <div className={clsx(
          'min-w-0 flex-1 flex-col bg-bg',
          'md:flex',
          selectedLeadUid ? 'flex' : 'hidden md:flex',
        )}>
          {!selectedLeadUid && (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-text-muted">
              <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-[28px] bg-success-soft text-success"><MessageCircle size={42} strokeWidth={1.4} /></div>
              <p className="text-2xl font-semibold tracking-tight text-text-primary">A better conversation starts here</p>
              <p className="mt-3 max-w-sm text-sm leading-relaxed">Reply personally, share your products, and let your team and AI work together.</p>
              <span className="mt-8 inline-flex items-center gap-1.5 text-xs"><ShieldCheck size={14} /> Your company’s private workspace</span>
            </div>
          )}

          {selectedLeadUid && (
            <>
              {/* Thread header */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface px-5 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  {/* Mobile-only back button — clears the lead query param to
                      show the inbox list again. Hidden on md+ where both
                      panes are always visible. */}
                  <button
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.delete('lead');
                      setParams(next, { replace: true });
                    }}
                    className="md:hidden -ml-1 rounded-lg p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <div className="min-w-0">
                    <div className="font-display text-[15px] font-semibold text-text-primary truncate">
                      {selectedLead ? leadDisplayName(selectedLead) : '…'}
                    </div>
                    <div className="text-xs text-text-muted">{selectedLead?.phone}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => setDeleteOpen(true)} title="Delete conversation from CRM" className="rounded-lg p-2 text-text-muted hover:bg-danger-soft hover:text-danger"><Trash2 size={15} /></button>
                  {selectedLead?.botFlowUid && detail?.lead.botFlowUid && (
                    <button
                      onClick={async () => {
                        try {
                          if (!selectedLeadUid) return;
                          // The lead has a bot flow attached; toggle pause/resume.
                          // The lead schema doesn't expose 'botPaused' directly here,
                          // so we just call resume — which is idempotent per the API.
                          await leadsService.resumeBot(selectedLeadUid);
                          await loadThread(selectedLeadUid);
                          toast.success('Bot resumed');
                        } catch { /* interceptor surfaces */ }
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-surface-alt"
                      title="Resume bot"
                    >
                      <BotOff size={12} /> Resume bot
                    </button>
                  )}

                  {/* AI fallback per-conversation toggle — only shown when no scripted flow is active */}
                  {!selectedLead?.botFlowUid && (
                    <button
                      onClick={async () => {
                        if (!selectedLeadUid) return;
                        const isPaused = !!detail?.lead.aiFallbackPaused;
                        try {
                          if (isPaused) await leadsService.resumeAiFallback(selectedLeadUid);
                          else await leadsService.pauseAiFallback(selectedLeadUid);
                          await loadThread(selectedLeadUid);
                          toast.success(isPaused ? 'AI replies resumed' : 'AI replies paused for this chat');
                        } catch { /* interceptor surfaces */ }
                      }}
                      className={clsx(
                        'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs transition-colors',
                        detail?.lead.aiFallbackPaused
                          ? 'border-warning/40 bg-warning-soft text-warning'
                          : 'border-violet-300 dark:border-violet-700/50 text-violet-600 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20',
                      )}
                      title={detail?.lead.aiFallbackPaused
                        ? 'AI auto-replies are paused for this conversation — click to resume'
                        : 'AI auto-replies are active for this conversation — click to pause and reply manually'}
                    >
                      <Sparkles size={12} />
                      {detail?.lead.aiFallbackPaused ? 'Resume AI' : 'AI on'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={toggleThreadMute}
                    className={clsx(
                      'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs transition-colors',
                      isThreadMuted
                        ? 'border-warning/40 bg-warning-soft text-warning'
                        : 'border-border hover:bg-surface-alt',
                    )}
                    title={isThreadMuted ? 'Unmute this conversation — sound + popups will resume' : 'Mute this conversation — sound + popups suppressed; toasts and badge still update'}
                  >
                    {isThreadMuted ? <BellOff size={12} /> : <Bell size={12} />}
                    {isThreadMuted ? 'Muted' : 'Mute'}
                  </button>
                  <Link
                    to={`/leads/${selectedLeadUid}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-surface-alt"
                  >
                    <ExternalLink size={12} /> Open lead
                  </Link>
                </div>
              </div>

              {/* Transcript */}
              <div ref={scrollRef} onScroll={() => { const el = scrollRef.current; if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100; }} className="relative flex-1 overflow-y-auto bg-[#f3f5f0] dark:bg-bg" style={{ backgroundImage: 'radial-gradient(rgba(90,110,80,.07) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                {detail?.hasOlderEvents && <div className="flex justify-center pt-4"><Button size="sm" variant="secondary" loading={loadingOlder} onClick={loadOlder}>Load earlier messages</Button></div>}
                {loadingThread && transcript.length === 0 && (
                  <div className="flex h-full items-center justify-center">
                    <Loader2 size={20} className="animate-spin text-text-muted" />
                  </div>
                )}
                {transcript.length > 0 && (
                  <div className="px-5 py-4">
                    <ConversationTranscript
                      messages={transcript}
                      onDebugClick={isAdmin ? setDebugLeadEventUid : undefined}
                    />
                  </div>
                )}
                {!loadingThread && transcript.length === 0 && (
                  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                    <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-surface-alt text-text-muted">
                      <MessageCircle size={20} />
                    </div>
                    <p className="text-sm font-semibold text-text-primary">No messages in this thread yet</p>
                    <p className="mt-1 max-w-[260px] text-xs text-text-muted">
                      Type below to send the first reply. Replies use the same WhatsApp account this lead came in on.
                    </p>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="border-t border-border bg-surface px-4 py-3">
                {/* 24h-window banner — free-form sends can't deliver, steer to templates */}
                {!composerEnabled && !loadingThread && detail && (
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2">
                    <Clock size={14} className="shrink-0 text-warning" />
                    <p className="min-w-0 flex-1 text-xs text-warning">
                      {detail.lead.lastInboundAt
                        ? 'WhatsApp’s 24-hour reply window has closed — free-text messages can’t be delivered.'
                        : 'This customer hasn’t messaged you yet — WhatsApp only allows template messages to start a conversation.'}
                      {' '}Send an approved template; the reply window opens when the customer responds.
                    </p>
                    <Button size="sm" variant="primary" onClick={() => setTemplatePickerOpen(true)}>
                      <FileText size={12} /> Send template
                    </Button>
                  </div>
                )}

                {/* Pending media attachment chip */}
                {pendingMedia && (
                  <div className="mb-2 flex items-center gap-3 rounded-lg border border-border bg-surface-alt px-3 py-2">
                    {pendingMedia.type === 'image' ? (
                      <img src={pendingMedia.url} alt="" className="h-12 w-12 rounded object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded bg-surface text-text-muted">
                        <Paperclip size={16} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-text-primary truncate">
                        {pendingMedia.filename ?? `${pendingMedia.type} attachment`}
                      </div>
                      <div className="text-2xs text-text-muted">
                        {pendingMedia.type === 'audio' ? 'Audio message · text is saved as transcript in CRM' : `Will be sent ${composerText.trim() ? 'with caption' : 'without caption'}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingMedia(null)}
                      className="rounded p-1 text-text-muted hover:bg-surface hover:text-danger"
                      title="Remove attachment"
                    >
                      <X size={13} />
                    </button>
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,video/mp4,video/3gpp,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={onFilePicked}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={onAttachClick}
                    disabled={uploadingMedia || sending || !composerEnabled}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-alt text-text-muted hover:border-border-strong hover:text-text-primary disabled:opacity-50"
                    title="Attach image / video / document"
                  >
                    {uploadingMedia
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Paperclip size={14} />}
                  </button>
                  <div className="relative">
                    <button type="button" onClick={() => setLibraryMenuOpen(!libraryMenuOpen)} disabled={sending || !composerEnabled} className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-surface-alt text-text-muted hover:text-text-primary disabled:opacity-50" title="Choose from your media library" aria-expanded={libraryMenuOpen}><Images size={14} /></button>
                    {libraryMenuOpen && <div className="absolute bottom-12 left-0 z-20 w-44 rounded-xl border border-border bg-surface p-1.5 shadow-lg">{(['image', 'video', 'document', 'audio'] as const).map((type) => <button key={type} type="button" onClick={() => { setLibraryType(type); setLibraryPickerOpen(true); setLibraryMenuOpen(false); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm capitalize hover:bg-surface-alt">{type === 'audio' ? 'Audio' : `${type}s`}</button>)}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => setTemplatePickerOpen(true)}
                    disabled={sending}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-alt text-text-muted hover:border-border-strong hover:text-text-primary disabled:opacity-50"
                    title="Send an approved template"
                  >
                    <FileText size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssetPickerOpen(true)}
                    disabled={sending || !composerEnabled}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-alt text-text-muted hover:border-border-strong hover:text-text-primary disabled:opacity-50"
                    title={composerEnabled
                      ? 'Share a product, catalog or WhatsApp form'
                      : 'Catalog sends are free-form messages — unavailable outside the 24h window'}
                  >
                    <ShoppingBag size={14} />
                  </button>
                  <textarea
                    ref={composerRef}
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    onKeyDown={onComposerKey}
                    disabled={!composerEnabled}
                    placeholder={
                      !composerEnabled
                        ? 'Free-text replies unavailable — use a template'
                        : pendingMedia
                          ? 'Add a caption (optional) — Enter to send'
                          : 'Type a message — Enter to send, Shift+Enter for new line'
                    }
                    rows={1}
                    className="max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:bg-surface focus:outline-none focus:ring-4 focus:ring-primary-soft disabled:opacity-60"
                  />
                  <Button
                    variant="primary"
                    onClick={send}
                    loading={sending}
                    disabled={(!composerText.trim() && !pendingMedia) || sending || !composerEnabled}
                  >
                    <Send size={14} />
                  </Button>
                </div>
                <p className="mt-1 text-2xs text-text-muted">
                  Messages are saved to CRM before sending. Delivery ticks update when WhatsApp confirms receipt.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal open={deleteOpen} onClose={() => { if (!deleting) setDeleteOpen(false); }} title="Delete this conversation?">
        <p className="text-sm leading-relaxed text-text-muted">This permanently removes the CRM conversation history for {selectedLead?.phone} on this WhatsApp account. The CRM lead record and messages on the customer’s phone are unaffected. A new inbound message starts a new conversation.</p>
        <div className="mt-5 flex justify-end gap-2"><Button variant="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button><Button variant="danger" loading={deleting} onClick={deleteConversation}>Delete conversation</Button></div>
      </Modal>

      {/* Approved-template picker — agent template sends from the composer */}
      {detail && (
        <TemplatePickerModal
          open={templatePickerOpen}
          onClose={() => setTemplatePickerOpen(false)}
          lead={detail.lead}
          onSent={() => { if (selectedLeadUid) loadThread(selectedLeadUid); }}
        />
      )}

      {detail && <WhatsAppAssetPicker open={assetPickerOpen} onClose={() => setAssetPickerOpen(false)} lead={detail.lead} accountUid={activeAccount?.accountUid} onSent={() => { if (selectedLeadUid) loadThread(selectedLeadUid, true); }} onCrmCatalog={() => setCatalogPickerOpen(true)} />}

      {/* Catalog picker — send a custom object record as a formatted message */}
      {detail && (
        <CatalogPickerModal
          open={catalogPickerOpen}
          onClose={() => setCatalogPickerOpen(false)}
          lead={detail.lead}
          onSent={() => { if (selectedLeadUid) loadThread(selectedLeadUid); }}
        />
      )}

      {/* Choose a generated/uploaded image or video to attach to the reply. */}
      <MediaLibraryModal
        open={libraryPickerOpen} onClose={() => setLibraryPickerOpen(false)}
        folder={`whatsapp/chat/${libraryType === 'image' ? 'images' : libraryType === 'video' ? 'videos' : libraryType === 'document' ? 'documents' : 'audio'}`}
        type={libraryType} multiple={false} title={`Your ${libraryType} library`}
        onPick={([asset]) => { if (asset) setPendingMedia({ url: asset.url, type: libraryType, mime: asset.mime, filename: asset.name }); }}
      />

      {/* Admin-only AI debug snapshot for the clicked AI message. */}
      {isAdmin && (
        <AiDebugPanel
          leadEventUid={debugLeadEventUid}
          onClose={() => setDebugLeadEventUid(null)}
        />
      )}

      <NewChatDialog
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
        accountUid={activeAccount?.accountUid}
        onStarted={async (phone) => {
          // The send upserts the lead server-side. Look it up by phone and open
          // that thread — the agent shouldn't have to hunt for it in the list.
          await refreshList();
          try {
            const r = await leadsService.list({
              q: phone,
              accountUid: activeAccount?.accountUid,
              limit: 5,
            });
            const digits = phone.replace(/\D+/g, '');
            const match = r.items.find((l) => (l.phone ?? '').replace(/\D+/g, '') === digits);
            if (match) setParams({ lead: match.leadUid });
          } catch { /* the conversation is in the list either way */ }
        }}
      />
    </>
  );
}
